import queryClient from '@/lib/queryClient'
import { productQueries } from '@/lib/services/products/products.query'
import type {
  Product,
  ProductIndexFilters,
  ProductIndexResult,
  Variant,
} from '@/lib/services/products/products.types'

/** A product option the register can add. */
export interface CatalogOption {
  product: Product
  variant: Variant
}

/** What a scanned or typed code found in the catalog. */
export type ScannedCodeMatch<TOption = CatalogOption> =
  | { kind: 'none' }
  | { kind: 'one'; option: TOption }
  /** Shared SKUs can name more than one option; barcodes are unique per shop. */
  | { kind: 'several' }

interface ScannableOption {
  variant: { barcodes: readonly string[]; sku?: string | null }
}

/** Picks the option a code names: a barcode match wins over a SKU match, which ignores case. */
export function matchScannedCode<TOption extends ScannableOption>(
  options: readonly TOption[],
  code: string
): ScannedCodeMatch<TOption> {
  const trimmedCode = code.trim()
  const lowercaseCode = trimmedCode.toLowerCase()
  const barcodeMatches = options.filter(({ variant }) => variant.barcodes.includes(trimmedCode))
  const [match, ...otherMatches] =
    barcodeMatches.length > 0
      ? barcodeMatches
      : options.filter(({ variant }) => variant.sku?.trim().toLowerCase() === lowercaseCode)

  if (!match) {
    return { kind: 'none' }
  }

  return otherMatches.length === 0 ? { kind: 'one', option: match } : { kind: 'several' }
}

function catalogOptions(result: ProductIndexResult): CatalogOption[] {
  const products = Array.isArray(result) ? result : result.data

  return products.flatMap((product) =>
    (product.variants ?? []).map((variant) => ({ product, variant }))
  )
}

/** Finds a code among the products the register can sell, using the catalog's own filters. */
export async function lookupScannedCode({
  organizationId,
  code,
  filters,
}: {
  organizationId: number | string
  code: string
  filters: ProductIndexFilters
}): Promise<ScannedCodeMatch> {
  const result = await queryClient.fetchQuery({
    ...productQueries.index({ organizationId, filters: { ...filters, code: code.trim() } }),
    staleTime: 0,
  })

  return matchScannedCode(catalogOptions(result), code)
}
